import AddIcon from '@mui/icons-material/Add';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    Theme,
} from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import InputReference from 'src/components/InputReference';
import Loading from 'src/components/loading/Loading';
import QuantitySelector from 'src/components/QuantitySelector';
import { Battery, TCartItem } from 'src/stores/types/CartItemModel';
import Store from 'src/stores/RootStore';
import { ProductModel, TProductModel } from 'src/stores/types/ProductTypes';
import AnalyticsEcommerce from 'src/utils/analytics-ecommerce';
import SelectBattery from './SelectBattery';
import SelectSaddleAndGrip from './SelectSaddleAndGrip';
import SelectStrap from './SelectStrap';
import CONFIG from 'src/utils/config';
import { AppBrands, BREKR_BIKE_WITH_BATTERY_OPTION } from 'src/utils/constants';
import SelectBrekrBattery from './SelectBrekrBattery';
import SelectFrame from './SelectFrame';
import SelectCarrier from './SelectCarrier';

interface ProductFormProps extends WithStyles<typeof styles> {
    store?: typeof Store.Type;
    setCartTypeToBike: () => void;
    product: TProductModel;
}

const ProductForm = inject('store')(
    observer(({ classes, store, setCartTypeToBike, product }: ProductFormProps) => {
        const brand = CONFIG.APP_BRAND;
        const isQWIC = brand === AppBrands.QWIC;

        // BREKR customized series products
        const showDoubleBatteryCheckbox = !isQWIC && product.serie === 'B4000';
        const showCarrierOption = BREKR_BIKE_WITH_BATTERY_OPTION.has(product.serie ?? '');
        const showBatteryOption = isQWIC || BREKR_BIKE_WITH_BATTERY_OPTION.has(product.serie ?? '');

        const [reference, setReference] = useState('');
        const [quantity, setQuantity] = useState(1);
        const [required, setRequired] = useState(false);

        // If there is no brekr battery or frame selected, the product cannot be added to the cart
        const isSubmitDisabled =
            !product.productInformation.stockQuantity ||
            (!showBatteryOption && store?.products.isEmptyBrekrBattery) ||
            store?.products.isEmptyFrame ||
            (showCarrierOption && store?.products.isEmptyCarrier);
        const calculatedAddButtonClasses = classNames(classes.button, {
            [classes.buttonDisabled]: isSubmitDisabled,
        });

        const addProduct = () => {
            let battery: typeof Battery.CreationType | null = null;
            let saddleAndGrip = '';
            let strap = '';
            let frame = '';
            let brekrBattery = '';
            let carrier = '';

            if (
                (!store!.products.isEmptyBrekrBattery || store!.products.selectedBikeBatteryCode.length > 0) &&
                ProductModel.is(product)
            ) {
                battery = store!.products.getBatteryData(product, store!.products.selectedBikeBatteryCode);
                brekrBattery = store!.products.selectedBrekrBatteryCode;
                saddleAndGrip =
                    store?.products.selectedSaddleAndGripCode === 'empty'
                        ? ''
                        : store?.products.selectedSaddleAndGripCode;
                strap = store?.products.selectedStrapCode === 'empty' ? '' : store?.products.selectedStrapCode;
                frame = store?.products.selectedFrameColor === 'empty' ? '' : store?.products.selectedFrameColor;
                carrier = store?.products.selectedCarrierCode;
            } else {
                setRequired(true);
                return;
            }

            const newProduct = {
                id: `${product.id}`,
                // id: `${product.id}|${brekrBattery || battery?.code || ''}|${frame}|${carrier}`,
                type: product.type,
                product,
                battery,
                brekrBattery,
                isDoubleBatterySelected: store?.products.isDoubleBatterySelected,
                saddleAndGrip,
                strap,
                frame,
                carrier,
                quantity,
                reference,
            } as TCartItem;

            store!.cart.addCartItem(newProduct, quantity);
            setCartTypeToBike();
            store!.products.toggleProductDetailDialog(false);

            // Sending item added into cart event to GA
            AnalyticsEcommerce.getInstance()
                .clear()
                .addProductToCart({
                    id: product.id,
                    name: product.name,
                    list: 'Products List',
                    brand: brand.toUpperCase(),
                    category: `${product.serie}/${product.model}`,
                    variant: product.productInformation?.color || undefined,
                    price: product.getPrice(),
                    quantity: quantity,
                    batteryCode: brekrBattery ?? battery?.code,
                })
                .sendPageView();

            setQuantity(1);
            setReference('');
        };

        if (store?.products.loadingConfigurableParts) {
            return <Loading small />;
        }

        return (
            <div className={classes.actions}>
                {showBatteryOption && (
                    <div className={classes.itemAction} style={{ marginBottom: '10px' }}>
                        <div className={classes.battery}>
                            <FormControl className={classes.batteryField} variant="standard">
                                <InputLabel htmlFor="battery-item">
                                    <FormattedMessage id="select.battery" />
                                </InputLabel>
                                <SelectBattery
                                    selectedBatteryCode={store!.products.selectedBikeBatteryCode}
                                    product={product}
                                    setSelectedBattery={(batteryCode: string) => {
                                        setRequired(false);
                                        store!.products.setSelectedBikeBatteryCode(batteryCode);
                                    }}
                                />
                                {required && (
                                    <FormHelperText className={classes.batteryAlert}>
                                        <FormattedMessage id="alert.select_battery" />
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </div>
                    </div>
                )}

                {isQWIC && (
                    <>
                        <Grid container columnSpacing={3}>
                            <Grid item xs={12} md={6}>
                                <SelectSaddleAndGrip
                                    selectedSaddleAndGripCode={store?.products.selectedSaddleAndGripCode || ''}
                                    setSelectedSaddleAndGripCode={store?.products.setSelectedSaddleAndGripCode}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <SelectStrap
                                    selectedStrapCode={store?.products.selectedStrapCode || ''}
                                    setSelectedStrapCode={store?.products.setSelectedStrapCode}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

                {!isQWIC && (
                    <>
                        <SelectBrekrBattery
                            selectedBrekrBatteryCode={store?.products.selectedBrekrBatteryCode || ''}
                            setSelectedBrekrBatteryCode={store?.products.setSelectedBrekrBatteryCode}
                        />
                        <SelectFrame
                            selectedFrameCode={store?.products.selectedFrameColor || ''}
                            setSelectedFrameCode={store?.products.setSelectedFrameColor}
                        />
                        <SelectCarrier
                            selectedCarrierCode={store?.products.selectedCarrierCode || ''}
                            setSelectedCarrierCode={store?.products.setSelectedCarrierCode}
                        />
                        {showDoubleBatteryCheckbox && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={store?.products.isDoubleBatterySelected}
                                        onChange={e => store?.products.setIsDoubleBatterySelected(e.target.checked)}
                                    />
                                }
                                label={<FormattedMessage id="product_dialog.double_battery" />}
                            />
                        )}
                    </>
                )}

                <div className={classes.itemAction} style={{ flexDirection: 'row' }}>
                    <Grid container spacing={3}>
                        <Grid item lg={12} sm={12}>
                            <InputReference reference={reference} updateReference={setReference} />
                        </Grid>
                    </Grid>
                    <div className={classes.quantity}>
                        <QuantitySelector
                            quantity={quantity}
                            maxQuantity={product.productInformation.stockQuantity}
                            updateQuantity={setQuantity}
                            isLabelBelow={true}
                        />
                    </div>
                </div>

                <Grid container spacing={3} className={classes.buttons}>
                    <Grid item xs={12} sm={6}>
                        <Button
                            onClick={() => store!.products.toggleProductDetailDialog()}
                            variant="outlined"
                            fullWidth
                            className={classes.button}
                        >
                            <FormattedMessage id="returns.form_label_cancel" />
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            data-testid="add-product-dialog"
                            disabled={isSubmitDisabled}
                            className={calculatedAddButtonClasses}
                            color="primary"
                            variant="contained"
                            fullWidth
                            onClick={addProduct}
                        >
                            <FormattedMessage id="dialog.addToCart" />
                            <AddIcon />
                        </Button>
                    </Grid>
                </Grid>
            </div>
        );
    }),
);

function styles({ spacing, palette }: Theme) {
    return createStyles({
        actions: {
            display: 'flex',
            flexDirection: 'column',
            margin: '1rem 0',
        },
        itemAction: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            marginBottom: '2rem',
        },
        quantity: {
            display: 'flex',
            flex: 1,
            marginLeft: '20px',
            marginTop: spacing(3),
        },
        battery: {
            display: 'flex',
            flex: 1,
        },
        batteryField: {
            flex: 1,
        },
        buttons: {
            display: 'flex',
            justifyContent: 'flex-end',
        },
        button: {
            paddingTop: '1em',
            paddingBottom: '1em',
            paddingLeft: '2em',
            paddingRight: '2em',
            justifyContent: 'space-between',
        },
        buttonDisabled: {
            'pointer-events': 'none',
        },
        batteryAlert: {
            color: palette.secondary.main,
            position: 'absolute',
            top: '100%',
        },
    });
}

const ProductFormWithStyles = withStyles(styles)(ProductForm);

export default ProductFormWithStyles;
